/* eslint-disable functional/immutable-data */

import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';
import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const LoadingAnimationBackgroundPulse = (props: Props) => {
  const {
    backgroundColor = DEFAULT_BACKGROUND_COLOR,
    foregroundColor = DEFAULT_FOREGROUND_COLOR,
    duration = DEFAULT_DURATION,
  } = props;

  //
  // ─── Shared Values ─────────────────────────────────────────────────────────

  const animationSV = useSharedValue(0);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    animationSV.value = withRepeat(
      withSequence(
        withTiming(1, { duration, easing: Easing.inOut(Easing.ease) }),
        withTiming(0, { duration, easing: Easing.inOut(Easing.ease) }),
      ),
      -1,
      true,
    );
  }, [animationSV, duration]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const backgroundContainerStyles = useStyle(
    () => ({ backgroundColor }),
    [backgroundColor],
  );
  const foregroundContainerStyles = useStyle(
    () => ({ backgroundColor: foregroundColor }),
    [foregroundColor],
  );
  const foregroundContainerAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: animationSV.value };
  });

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.backgroundContainer, backgroundContainerStyles]}>
      <Animated.View
        style={[
          styles.foregroundContainer,
          foregroundContainerStyles,
          foregroundContainerAnimatedStyles,
        ]}
      />
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_DURATION = 3000;
const DEFAULT_BACKGROUND_COLOR = theme.colors.DARK_KALE;
const DEFAULT_FOREGROUND_COLOR = theme.colors.OPACITY.KALE.DARKER;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backgroundContainer: {
    width: '100%',
    height: '100%',
  },
  foregroundContainer: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  backgroundColor?: string;
  foregroundColor?: string;
  duration?: number;
};
